import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import LicenseStatusBadge, { getLicenseStatus } from '@/components/atoms/LicenseStatusBadge';
import { BioscopeContractListOfBioscopes } from '@/components/molecules/BioscopeContractListOfBioscopes/BioscopeContractListOfBioscopes';
import { confirm } from '@/components/molecules/ConfirmDialog';
import ModalAddLicense, { type NewLicenseProps } from '@/components/molecules/ModalAddLicense';
import ModalUpdateLicense from '@/components/molecules/ModalUpdateLicense';
import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import type {
    Contract,
    CreateNewContractInput,
    CreateNewContractLicenseInput,
    LicenseInfo,
} from '@/domain/licenses';
import { formatDayDisplay } from '@/utils/date';

import ModalUpdateBioscopeContract from '../ModalUpdateBioscopeContract/ModalUpdateBioscopeContract';

import styles from './BioscopeContract.module.scss';

type ContractLicense = Contract['licenses'][number];

export interface MoveCallbackParameters {
    bioscopeId: number;
    cageId: number;
    clientId: number;
    from: string;
    contractId: number;
}

export interface BioscopeContractProps {
    contract: Contract;
    licenseList: LicenseInfo[];
    onCreateLicense: NewLicenseProps['onSubmitted'];
    onUpdateContract: (id: number, license: CreateNewContractInput) => void;
    onUpdateLicense: (licenseId: number, license: CreateNewContractLicenseInput) => void;
    /** These run inside a confirmation, which waits for them and shows a failure. */
    onDeleteLicense: (id: number) => Promise<unknown>;
    onArchiveContract: (id: number) => Promise<unknown>;
    onUnarchiveContract: (id: number) => Promise<unknown>;
    openMoveModal: (bioscopeId: number) => void;
    isArchived?: boolean;
}

export function BioscopeContract(props: BioscopeContractProps) {
    const {
        contract,
        licenseList,
        onCreateLicense,
        onUpdateLicense,
        onDeleteLicense,
        onArchiveContract,
        onUnarchiveContract,
        onUpdateContract,
        openMoveModal,
        isArchived = false,
    } = props;
    const { t } = useTranslation();

    const licenseColumns: DataTableColumnDef<ContractLicense>[] = [
        {
            id: 'name',
            header: t('common.labels.name'),
            cell: ({ row }) => row.original.license.name,
        },
        {
            id: 'from',
            header: t('common.labels.from'),
            cell: ({ row }) => formatDayDisplay(row.original.dayFrom),
        },
        {
            id: 'to',
            header: t('molecules.common.to'),
            cell: ({ row }) => formatDayDisplay(row.original.dayTo),
        },
        {
            id: 'status',
            header: t('common.labels.status'),
            cell: ({ row }) => (
                <LicenseStatusBadge
                    status={getLicenseStatus(row.original.dayFrom, row.original.dayTo)}
                />
            ),
        },
        {
            id: 'actions',
            header: t('common.labels.actions'),
            cell: ({ row }) => (
                <>
                    <ModalUpdateLicense
                        licenseData={{
                            dayFrom: row.original.dayFrom,
                            dayTo: row.original.dayTo,
                            licenseId: row.original.license.id,
                        }}
                        licenses={licenseList}
                        onSubmitted={(input) => onUpdateLicense(row.original.id, input)}
                    />
                    <Button
                        variant={'danger'}
                        onClick={() =>
                            confirm({
                                title: t('internal.clientLicense.deleteTitle'),
                                message: t('bioscopeContracts.licenseModal.confirmDelete'),
                                confirmLabel: t('common.actions.delete'),
                                onConfirm: () => onDeleteLicense(row.original.id),
                            })
                        }>
                        {t('common.actions.deleteEntry')}
                    </Button>
                </>
            ),
        },
    ];
    return (
        <div className={styles.BioscopeContract}>
            <div className={styles.addLicense}>
                <ModalUpdateBioscopeContract
                    contractData={contract}
                    onSubmitted={onUpdateContract}
                />
                <ModalAddLicense licenses={licenseList} onSubmitted={onCreateLicense} />
                <Button
                    variant={isArchived ? 'primary' : 'secondary'}
                    onClick={() =>
                        confirm({
                            title: isArchived
                                ? t('bioscopeContracts.contract.unarchive')
                                : t('bioscopeContracts.contract.archive'),
                            message: isArchived
                                ? t('bioscopeContracts.contract.confirmUnarchive')
                                : t('bioscopeContracts.contract.confirmArchive'),
                            confirmLabel: isArchived
                                ? t('bioscopeContracts.contract.unarchive')
                                : t('bioscopeContracts.contract.archive'),
                            confirmTone: 'primary',
                            onConfirm: () =>
                                isArchived
                                    ? onUnarchiveContract(contract.id)
                                    : onArchiveContract(contract.id),
                        })
                    }>
                    {isArchived
                        ? t('bioscopeContracts.contract.unarchive')
                        : t('bioscopeContracts.contract.archive')}
                </Button>
            </div>
            <DataTable
                data={contract.licenses}
                columns={licenseColumns}
                getRowId={(license) => String(license.id)}
                enableSorting={false}
                hover={false}
            />
            <BioscopeContractListOfBioscopes
                bioscopes={contract.bioscopeLocations}
                openMoveModal={openMoveModal}
            />
        </div>
    );
}
